import React, { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { ThemedText } from '@/components/themed-text';
import { ChargeStatusBadge } from '@/components/charges/charge-status-badge';
import { DisputeCard } from '@/components/disputes/dispute-card';
import { useDisputes } from '@/hooks/use-disputes';
import { useCharge } from '@/hooks/use-charges';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { Ionicons } from '@expo/vector-icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { PageLayout } from '@/components/ui/page-layout';
import { SkeletonCard } from '@/components/ui/skeleton';
import { RefundTimeline } from '@/components/charges/refund-timeline';
import { Button } from '@/components/ui/button';
import { router } from 'expo-router';
import { useFocusParam } from '@/hooks/use-focus-param';
import { FOCUS_PARAM } from '@/lib/notification-navigation';

const disputeTips = [
  {
    icon: 'link-outline' as const,
    text: 'Revise a cobrança e o histórico de pagamentos antes de responder.',
  },
  {
    icon: 'document-text-outline' as const,
    text: 'Guarde comprovantes, conversas e termos como evidência.',
  },
  {
    icon: 'refresh-outline' as const,
    text: 'Um reembolso parcial pode resolver a contestação.',
  },
];

export default function DisputesScreen() {
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);

  const { data: disputes, isLoading, error, refetch } = useDisputes();
  const { data: selectedCharge } = useCharge(selectedChargeId || undefined);

  // Deep link de notificação de contestação: abre o detalhe da cobrança avisada.
  const [focusChargeId, clearFocusCharge] = useFocusParam(FOCUS_PARAM.charge);
  useEffect(() => {
    if (focusChargeId) setSelectedChargeId(focusChargeId);
  }, [focusChargeId]);

  const handleDisputePress = (chargeId: string) => {
    setSelectedChargeId(chargeId);
  };

  return (
    <>
      <PageLayout title="Disputas" subtitle="Cobranças contestadas pelos clientes">
        {isLoading ? (
          <View className="gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} showHeader={true} lines={3} />
            ))}
          </View>
        ) : error ? (
          <ErrorState
            variant="inline"
            title="Erro ao carregar disputas"
            message="Não foi possível carregar a lista de disputas."
            onRetry={refetch}
          />
        ) : !disputes || disputes.length === 0 ? (
          <EmptyState
            icon="checkmark-circle-outline"
            title="Nenhuma disputa"
            description="Ótimo! Nenhum cliente contestou suas cobranças."
            className="py-16"
          />
        ) : (
          <View className="gap-3">
            {disputes.map((dispute, index) => (
              <FadeInView key={dispute.id} delay={index * 50}>
                <DisputeCard dispute={dispute} onPress={handleDisputePress} />
              </FadeInView>
            ))}
          </View>
        )}
      </PageLayout>

      {/* Detalhe da disputa */}
      {selectedCharge && (
        <Dialog
          open={!!selectedChargeId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedChargeId(null);
              clearFocusCharge();
            }
          }}
        >
          <DialogContent className="max-h-[90%]">
            <DialogHeader>
              <DialogTitle>Detalhes da disputa</DialogTitle>
              <DialogDescription>
                <ThemedText className="text-sm text-neutral-500" selectable>
                  ID: {selectedCharge.id}
                </ThemedText>
              </DialogDescription>
            </DialogHeader>

            <ScrollView keyboardShouldPersistTaps="handled" className="max-h-[70vh]">
              <View className="mt-4 gap-4">
                <View
                  className="flex-row items-start gap-3 rounded-2xl bg-warning-50 p-4"
                  style={{ borderCurve: 'continuous' }}
                >
                  <Ionicons className="text-warning-600" name="warning" size={20} />
                  <ThemedText className="flex-1 text-sm leading-5 text-warning-600">
                    O cliente contestou esta cobrança. Revise os detalhes e entre em contato se
                    necessário.
                  </ThemedText>
                </View>

                <View
                  className="gap-3 rounded-2xl bg-neutral-50 p-4"
                  style={{ borderCurve: 'continuous' }}
                >
                  <View className="flex-row items-center justify-between">
                    <ThemedText className="text-sm text-neutral-500">Valor</ThemedText>
                    <ThemedText
                      className="font-bold text-xl text-foreground"
                      style={{ fontVariant: ['tabular-nums'] }}
                    >
                      {formatCurrency(selectedCharge.amount)}
                    </ThemedText>
                  </View>
                  <View className="flex-row items-center justify-between">
                    <ThemedText className="text-sm text-neutral-500">Status</ThemedText>
                    <ChargeStatusBadge status={selectedCharge.status} />
                  </View>
                  <View className="flex-row items-center justify-between">
                    <ThemedText className="text-sm text-neutral-500">Vencimento</ThemedText>
                    <ThemedText className="text-sm text-foreground">
                      {formatDate(selectedCharge.dueDate)}
                    </ThemedText>
                  </View>
                  {selectedCharge.paidAt && (
                    <View className="flex-row items-center justify-between">
                      <ThemedText className="text-sm text-neutral-500">Pago em</ThemedText>
                      <ThemedText className="text-sm text-foreground">
                        {formatDate(selectedCharge.paidAt)}
                      </ThemedText>
                    </View>
                  )}
                </View>

                {(selectedCharge.refundedAmount || 0) > 0 && (
                  <RefundTimeline charge={selectedCharge} />
                )}

                <View className="gap-2.5">
                  <ThemedText className="font-semibold text-sm">Como agir</ThemedText>
                  {disputeTips.map((tip) => (
                    <View key={tip.icon} className="flex-row items-start gap-2.5">
                      <Ionicons
                        name={tip.icon}
                        size={16}
                        color="#FD5001"
                        style={{ marginTop: 2 }}
                      />
                      <ThemedText className="flex-1 text-sm leading-5 text-neutral-600">
                        {tip.text}
                      </ThemedText>
                    </View>
                  ))}
                  <Button
                    variant="outline"
                    title="Ver cobrança detalhada"
                    onPress={() => router.push('/(tabs)/(provider)/charges')}
                    className="mt-1"
                  />
                </View>
              </View>
            </ScrollView>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
